// Cost price, COGS and gross margin — the one definition every admin view reads.
//
// Cost is snapshotted onto an order's lines when the sale is made, so the margin
// shown on a completed sale is the margin it was actually made at. Margin is
// always worked out against the ex-VAT selling price (gross ÷ 1.2) so it compares
// like for like with the ex-VAT cost price.

export const VAT_RATE = 0.2;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Selling price excluding VAT — the basis every margin figure is worked out on. */
export const exVatPrice = (gross) => round2((Number(gross) || 0) / (1 + VAT_RATE));

/** Postage is never part of the goods margin, so it is excluded from COGS. */
export const isCarriage = (line) =>
  String(line?.description || '').toLowerCase().startsWith('shipping');

/** Unit margin (£) and margin (%) for a product, from its gross price and cost. */
export function productMargin(product) {
  const cost = product?.cost_price_ex_vat;
  const hasCost = typeof cost === 'number';
  const exVat = product?.price != null ? exVatPrice(product.price) : null;
  if (exVat == null) return { exVat: null, cost: null, margin: null, marginPct: null, hasCost };
  const margin = hasCost ? round2(exVat - cost) : null;
  const marginPct = hasCost && exVat > 0 ? (margin / exVat) * 100 : null;
  return { exVat, cost: hasCost ? cost : null, margin, marginPct, hasCost };
}

/** Margin tone, matching the treatment already used in Stock Control. */
export const marginTone = (pct) =>
  pct == null
    ? 'text-white/25'
    : pct < 0
      ? 'text-red-400 font-bold'
      : pct < 20
        ? 'text-amber-400 font-bold'
        : 'text-green-400 font-bold';

/** Net goods revenue on an order, excluding VAT and postage. */
export function orderNet(order) {
  const lines = Array.isArray(order?.line_items) ? order.line_items : [];
  const goods = lines.filter((li) => !isCarriage(li));
  if (goods.length) return round2(goods.reduce((s, li) => s + (Number(li.net) || 0), 0));
  // No per-line breakdown (a booking, or a legacy single-line record): derive it
  // from the amount charged, less any postage.
  const gross = Math.max(0, (Number(order?.amount) || 0) - (Number(order?.shipping_amount) || 0));
  return exVatPrice(gross);
}

/**
 * COGS and gross margin for one order, read from the cost snapshotted at sale.
 *
 * `missingCost` marks a goods sale that carries no cost anywhere, and `partial`
 * one where only some lines had a cost — both are flagged in the admin views so a
 * missing cost is never mistaken for a 100% margin.
 */
export function orderCostSummary(order) {
  const lines = Array.isArray(order?.line_items) ? order.line_items : [];
  const goods = lines.filter((li) => !isCarriage(li));
  const net = orderNet(order);

  const stored = typeof order?.cost_total === 'number' ? order.cost_total : null;
  const lineCosts = goods.filter((li) => typeof li.line_cost === 'number');

  let cogs = null;
  if (order?.cost_recorded === true && stored != null) cogs = round2(stored);
  else if (lineCosts.length) cogs = round2(lineCosts.reduce((s, li) => s + Number(li.line_cost), 0));

  const margin = cogs == null ? null : round2(net - cogs);
  const marginPct = margin != null && net > 0 ? (margin / net) * 100 : null;

  // A booking is a service sale with no stock behind it, so it is not flagged as
  // missing a cost — only a sale that actually carries goods can be.
  const carriesGoods = goods.length > 0 || !!order?.product_id;

  return {
    net,
    cogs,
    margin,
    marginPct,
    backfilled: !!order?.cost_backfilled,
    missingCost: cogs == null && carriesGoods,
    partial: cogs != null && goods.length > 0 && lineCosts.length < goods.length,
  };
}

/** Per-line cost and margin, for the order detail breakdown. */
export function lineMargin(line) {
  const net = Number(line?.net) || 0;
  const cost = typeof line?.line_cost === 'number' ? line.line_cost : null;
  const margin = cost == null ? null : round2(net - cost);
  return { net: round2(net), cost, margin };
}